import axios from 'axios';

import { calculateLiaCompleteness } from 'config/api/lia/lia.helpers';
import { mapLiaToUpdateInput } from 'config/api/lia/lia.parsers';
import type { Lia, LiaSafeguardCode, UpdateLiaInput } from 'config/api/lia/lia.types';
import { extractUniqueValidationMessages } from 'config/api/processingActivities/processingActivities.parsers';

export type PurposeLiaFormValues = UpdateLiaInput;

export type PurposeLiaValidationMessages = {
  required: string;
  vulnerableComment: string;
};

export const mapLiaToFormValues = (lia: Lia): PurposeLiaFormValues => mapLiaToUpdateInput(lia);

export const mapFormValuesToUpdateInput = (values: PurposeLiaFormValues): UpdateLiaInput => values;

export const buildCompletenessItems = (values: PurposeLiaFormValues) => calculateLiaCompleteness(values);

export const validatePurposeLiaForm = (
  values: PurposeLiaFormValues,
  messages: PurposeLiaValidationMessages,
): Partial<Record<string, string>> => {
  const errors: Partial<Record<string, string>> = {};

  if (values.vulnerableDataSubjectsInvolved && !values.vulnerableDataSubjectsComment.trim()) {
    errors.vulnerableDataSubjectsComment = messages.vulnerableComment;
  }

  if (!values.conclusion) {
    errors.conclusion = messages.required;
  }

  return errors;
};

/**
 * Reads the reason from a failed LIA request (e.g. the purpose legal basis is not `gdpr_6_1_f`),
 * so the user sees the backend explanation instead of a generic failure message.
 */
export const getLiaApiErrorMessage = (error: unknown): string | undefined => {
  if (!axios.isAxiosError(error)) return undefined;

  const { data } = error.response ?? {};
  // Non-JSON bodies (proxy/HTML error pages) are never user-readable.
  if (typeof data === 'string') return undefined;

  const messages = extractUniqueValidationMessages(data);
  return messages.length > 0 ? messages.join(' ') : undefined;
};

/** A 4xx response describes a permanent problem with the request itself, so repeating it cannot help. */
export const isRetryableLiaApiError = (error: unknown): boolean => {
  if (!axios.isAxiosError(error)) return true;

  const status = error.response?.status;
  if (status === undefined) return true;

  return status === 408 || status === 429 || status >= 500;
};

export const toggleSafeguard = (selected: LiaSafeguardCode[], code: LiaSafeguardCode): LiaSafeguardCode[] =>
  selected.includes(code) ? selected.filter(item => item !== code) : [...selected, code];

export const isLiaReadOnly = (lia: Lia): boolean => lia.isDeleted;
